import React from "react";
import PageWrapper, { PageHero } from "../components/PageWrapper";
import LeadForm from "../components/LeadForm";
import {
  Cpu, BookOpen, Brain, Video, Library, Users, ShieldCheck,
  MessagesSquare, CloudUpload, Languages, Smartphone, BarChart3, GraduationCap,
} from "lucide-react";

const FEATURES = [
  { icon: BookOpen, title: "Learning Management System", desc: "Course delivery, content libraries, academic + extracurricular integration in one place." },
  { icon: Brain, title: "AI-Powered Assessments", desc: "Adaptive tests, AI evaluation, and meaningful insights — not just scores." },
  { icon: Video, title: "Live Classes & Recordings", desc: "Native live class infrastructure with seamless recording and replay." },
  { icon: Library, title: "Digital Library with OCR", desc: "Searchable institutional library with OCR across scanned and printed materials." },
  { icon: BarChart3, title: "Student Analytics Dashboard", desc: "Outcome dashboards for students, classes, and the entire institution." },
  { icon: Users, title: "Parent & Teacher Portals", desc: "Visibility for parents, control for teachers, governance for leadership." },
  { icon: ShieldCheck, title: "AI-Proctored Exams", desc: "Secure, scalable proctoring for high-stakes assessments." },
  { icon: MessagesSquare, title: "Communication Tools", desc: "Announcements, messaging, and collaboration unified across roles." },
  { icon: CloudUpload, title: "Secure Cloud Storage", desc: "Institution-grade storage with role-based access and auditing." },
  { icon: Languages, title: "Multi-Language Support", desc: "Designed for diverse linguistic and regional contexts." },
  { icon: Smartphone, title: "Web + Mobile Applications", desc: "Native experiences for staff, students, parents, and leadership." },
  { icon: GraduationCap, title: "Teacher Management System", desc: "Workforce tools for academic operations, scheduling, and compliance." },
];

export default function SchoolOSPage() {
  return (
    <PageWrapper testId="school-os-page">
      <PageHero
        eyebrow="School OS"
        title="The complete operating system for modern educational institutions."
        description="End-to-end digital infrastructure — academic, administrative, and analytical — unified into a single institutional platform."
        image="https://static.prod-images.emergentagent.com/jobs/a40b2e9c-8f18-467c-802c-d2ada63af7ec/images/be9ec9117222c24d87e66c3b257854b2ee3b782a23188879acf4cabb7db0e847.png"
      />

      <section className="bg-white py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Architecture, not tools
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight">
              One ecosystem. Every academic and operational workflow.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3 space-y-5 text-slate-600 leading-relaxed text-base sm:text-lg">
            <p>
              School OS is built around a simple thesis: an institution should
              run on architecture, not a stack of disconnected tools. We unify
              learning, assessment, communication, and analytics under one
              digital fabric.
            </p>
            <p>
              The result is institutional clarity — leaders see outcomes,
              teachers reclaim time, parents gain visibility, and students
              learn in environments designed for their century, not the last.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
            Capabilities
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight max-w-3xl">
            What's included in School OS.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`schoolos-feature-${i}`}
                className="card-lift bg-white border border-slate-200 p-7"
              >
                <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center">
                  <f.icon size={18} strokeWidth={1.5} />
                </div>
                <div className="mt-6 font-display text-lg text-navy">{f.title}</div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {f.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
              For institutions
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
              Bring School OS to your institution.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Onboarding is led by our solutions team — discovery, configuration,
              data migration, training, and go-live within a structured timeline.
            </p>
          </div>
          <div className="lg:col-span-7">
            <LeadForm
              inquiryType="institutional"
              variant="dark"
              testIdPrefix="schoolos-lead-form"
              title="Request a School OS consultation"
              subtitle="Tell us about your institution. We'll schedule a tailored discovery."
            />
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
